/**
 * diffDedup — collapses Review-Changes items that carry identical
 * old AND identical new content. Two narrative fields rerun against
 * the same evidence frequently end up with byte-identical output;
 * surfacing both as separate cards forces the user to make the
 * same decision twice.
 *
 * The collapsed entry retains the first item's section + label and
 * tags `appliesTo` with every section id that shared the content.
 * Callers render `appliesTo` as a "(applies to: A, B)" footnote.
 */

export interface DedupItem {
  section: string;
  label: string;
  oldValue: string;
  newValue: string;
  // Other per-item fields are passed through verbatim.
  [k: string]: unknown;
}

export interface DedupedItem<T extends DedupItem> {
  primary: T;
  appliesTo: string[];
}

export function dedupIdenticalDiffs<T extends DedupItem>(items: T[]): DedupedItem<T>[] {
  const out: DedupedItem<T>[] = [];
  const seen = new Map<string, DedupedItem<T>>();

  for (const item of items) {
    // Only collapse identical TEXT pairs — score / status changes
    // already carry distinct semantic meaning per section even when
    // the strings happen to match.
    const key = `${item.oldValue}\x00${item.newValue}`;
    // Trivially-empty pairs aren't deduplicated — both sides empty
    // typically means the item shouldn't have been emitted at all,
    // but we don't want to silently merge unrelated empty entries.
    if (!item.oldValue && !item.newValue) {
      out.push({ primary: item, appliesTo: [item.section] });
      continue;
    }
    const existing = seen.get(key);
    if (existing) {
      existing.appliesTo.push(item.section);
    } else {
      const entry: DedupedItem<T> = { primary: item, appliesTo: [item.section] };
      seen.set(key, entry);
      out.push(entry);
    }
  }

  return out;
}
